import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { Button, Container, UncontrolledTooltip } from "reactstrap";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";

import ConfigModal from "Pages/Admin/AdminConfiguration/AdminConfigModal/AdminConfigModal";
import ReactTable from "components/ReactTable/ReactTable";
import useFetch from "Hooks/useFetch";
import styles from "Pages/Admin/AdminConfiguration/AdminConfiguration.module.css";
import DefaultColumnFilter from "components/ReactTable/Components/DefaultColumnFilter/DefaultColumnFilter";
import useHelpers from "Hooks/useHelpers";
import usePageTitle from "Hooks/usePageTitle";

type ActionType = "new" | "edit" | "delete" | "";
export interface IAdminConfiguration {
    id: number;
    company_id: string;
    purpose: string;
    key: string;
    value: string;
    data_type: string;
    is_obscured: boolean | undefined;
    created_date: Date | undefined;
}

export default function AdminConfiguration() {
    const [configs, setConfigs] = useState<IAdminConfiguration[]>([]);
    const [activeModal, setActiveModal] = useState<ActionType>("");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(undefined);

    usePageTitle("Admin Configuration");
    const { get, post } = useFetch();
    const { copyToClipboard } = useHelpers();

    const ignore = useRef(false);

    /** Fetches all config values*/
    const getAdminConfigs = useCallback(
        async (ignore: boolean) => {
            if (ignore) {
                return;
            }
            setIsLoading(true);

            const response = await get(`/api/admin/config`);

            let data: Response | IAdminConfiguration[];
            try {
                data = await response.json();
            } catch (e) {
                data = response;
            }
            if (ignore) return;

            if (response.ok) {
                data = data as IAdminConfiguration[];
                setConfigs(data);
            }

            setIsLoading(false);
        },
        [get]
    );

    /** Calls the fetch getAdminConfigs function on page load */
    useEffect(() => {
        ignore.current = false;

        getAdminConfigs(ignore.current);

        return () => {
            ignore.current = true;
        };
    }, [get, getAdminConfigs, post]);

    /** Wraps state setting callback to pass to child component and update state from parent */
    const onSetActiveModal = useCallback((modal: ActionType) => {
        setActiveModal(modal);
    }, []);

    /** Wraps state setting callback to pass to child component and update state from parent */
    function onSetSelectedRow(row: any) {
        setSelectedRow(row);
    }

    /** Passed down to table as rowDoubleClickCallback */
    const editRow = useCallback((row: any) => {
        setActiveModal("edit");
        setSelectedRow(row);
    }, []);

    const columns = useMemo(() => {
        const cellContentCopy = (row: any, value: string) => {
            const id = "copyBtn-" + row.id;
            const isObscured = row.original?.is_obscured;
            const displayValue = isObscured && value ? "••••••••" : value;
            return (
                <>
                    <FontAwesomeIcon
                        id={id}
                        className={styles.copyIcon}
                        onClick={() => copyToClipboard(value)}
                        icon={faCopy}
                    />
                    <UncontrolledTooltip target={id}>
                        Copy to clipboard
                    </UncontrolledTooltip>
                    <span className={styles.textBox}>{displayValue}</span>
                </>
            );
        };

        return [
            {
                Header: "Company ID",
                accessor: "company_id",
                // Not using default filter, so as to allow for auto focusing this input
                Filter: ({ column }: any) => {
                    return <DefaultColumnFilter column={column} autoFocus />;
                },
            },
            {
                Header: "Purpose",
                accessor: "purpose",
            },
            {
                Header: "Setting",
                accessor: "keyName",
            },
            {
                Header: "Value",
                accessor: "keyValue",
                Cell: ({ row, value }: { row: any; value: string }) =>
                    cellContentCopy(row, value),
            },
            {
                Header: "Data Type",
                accessor: "data_type",
            },
            {
                Header: "Hidden",
                accessor: "is_obscured",
                Cell: ({ value }: { value: boolean }) =>
                    value ? "True" : "False",
            },
            {
                Header: "Date Modified",
                accessor: "modified_date",
                Cell: ({ value }: { value: Date }) =>
                    format(new Date(value), "MMM d, yyyy, HH:mm:ss"),
            },
            {
                Header: "Date Created",
                accessor: "created_date",
                Cell: ({ value }: { value: Date }) =>
                    format(new Date(value), "MMM d, yyyy, HH:mm:ss"),
            },
            {
                Header: "hidden",
                accessor: "id",
            },
        ];
    }, [copyToClipboard]);

    const tableProps = {
        columns,
        data: configs,
        showPagination: true,
        onSetSelectedRow,
        rowDoubleClickCallback: editRow,
        isLoading,
    };

    const adminConfigModalProps = {
        activeModal,
        getAdminConfigs,
        onSetActiveModal,
        onSetSelectedRow,
        selectedRow,
    };

    return (
        <Container fluid>
            {/* Header and New/Edit/Delete buttons */}
            <div className={styles.headerAndButtonsBox}>
                <h1>All Configurations</h1>
                <span className={styles.buttonsBox}>
                    <Button
                        color="primary"
                        onClick={() => setActiveModal("new")}
                    >
                        New
                    </Button>
                    <Button
                        color="success"
                        onClick={() => setActiveModal("edit")}
                    >
                        Edit
                    </Button>
                    <Button
                        color="danger"
                        onClick={() => setActiveModal("delete")}
                    >
                        Delete
                    </Button>
                </span>
            </div>

            <ReactTable {...tableProps} />

            <ConfigModal {...adminConfigModalProps} />
        </Container>
    );
}
